// src/pages/ChatBot/ChatInterface.tsx

import React, { useState, useRef, useEffect } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatInterface.css';

// GitHub-flavored markdown: turns the model's pipe-tables, task lists, and
// strikethrough into real elements. Without it react-markdown's plain CommonMark
// parser leaves `| a | b |` tables as raw text that wraps and breaks on narrow
// (mobile) screens.
const MARKDOWN_PLUGINS = [remarkGfm];

// Must match the exact string returned by the backend /ask fallback
const LEARNING_FALLBACK =
  "I'm still learning and I don't have an answer for that right now, give me a few days and check back.";

// Which corpus the question is asked against. 'documents' is the original (and
// default) behaviour — the uploaded document corpus. 'data' asks against the
// org's structured application records and is gated on the `chatbot.data`
// permission; the server enforces it too, this only decides what's on screen.
type ChatMode = 'documents' | 'data';

// Sub-feature permission that unlocks Data mode — a key hanging off `chatbot`
// (the user-facing module permission, not chatbot.admin: managing the chat
// console and querying data are separate grants). Checked here for display, and
// by checkPermission() on every /chatbot/data route.
const DATA_MODE_PERMISSION = 'chatbot.data';

const ASK_ENDPOINT: Record<ChatMode, string> = {
  documents: '/chatbot/ask',
  data: '/chatbot/data/ask',
};

type FeedbackChoice = 'up' | 'down';

// Numeric signal the backend stores on the query log: 1 thumbs-up, -1 thumbs-down,
// 0 = the thumb was reverted to neutral.
type FeedbackRating = 1 | -1 | 0;

const FEEDBACK_RATING: Record<FeedbackChoice, FeedbackRating> = { up: 1, down: -1 };

// Bot messages carry the queryId returned by /ask (identifies the query log the
// thumbs rate) and the question they answered (the backend's fallback matcher).
// contentMarked keeps the [N] citation markers for chip rendering; content stays
// the stripped text (feedback payloads + fallback rendering for older backends).
type BotMessage = {
  sender: 'bot';
  content: string;
  contentMarked?: string;
  citations?: { marker: number; documentName: string }[];
  sources?: string[];
  queryId?: string;
  question?: string;
};

type Message = { sender: 'user'; content: string } | BotMessage;

// A "run" of adjacent citation markers: one bracket group, then any number of
// further groups separated only by whitespace (e.g. "[1][1][1]", "[1] [2]",
// "[1, 3][2]").
const CITATION_RUN_RE = /\[\d+(?:\s*,\s*\d+)*\](?:\s*\[\d+(?:\s*,\s*\d+)*\])*/g;

// Rewrite the backend's raw passage markers into SOURCE numbers that match the
// numbered Sources list below the answer. Each passage marker is mapped to its
// document's source number (its 1-based position in the deduped sources list),
// so the same file always shows the same number and an inline "²" ties to
// "[2] File.pdf" in the list. Within an adjacent run, repeats of the same source
// collapse to one chip (order kept); markers with no known source are dropped.
// Output is a markdown link (#cite-N) the renderer turns into a non-interactive
// superscript chip.
const markCitations = (text: string, sourceNumberByMarker: Map<number, number>): string =>
  text.replace(CITATION_RUN_RE, run => {
    const markers = (run.match(/\d+/g) ?? []).map(Number);
    const seen = new Set<number>();
    const kept: number[] = [];
    for (const marker of markers) {
      const sourceNumber = sourceNumberByMarker.get(marker);
      if (sourceNumber === undefined || seen.has(sourceNumber)) continue;
      seen.add(sourceNumber);
      kept.push(sourceNumber);
    }
    return kept.map(n => `[${n}](#cite-${n})`).join('');
  });

// Bot answer body. With marked text, the backend's passage markers render as
// small superscript SOURCE numbers that match the numbered Sources list below the
// answer — no tooltip, since hover doesn't work on touch. Older backends without
// answerMarked fall back to the stripped text with no chips.
function AnswerMarkdown({ message }: { message: BotMessage }) {
  // Number each unique source 1..N in the order the Sources list shows them, then
  // map every passage marker to its source's number so the inline chips and the
  // list share one numbering.
  const orderedSources =
    message.sources ?? [...new Set((message.citations ?? []).map(c => c.documentName))];
  const sourceNumberByDocument = new Map<string, number>(
    orderedSources.map((doc, index): [string, number] => [doc, index + 1]),
  );
  const sourceNumberByMarker = new Map<number, number>();
  for (const citation of message.citations ?? []) {
    const sourceNumber = sourceNumberByDocument.get(citation.documentName);
    if (sourceNumber !== undefined) sourceNumberByMarker.set(citation.marker, sourceNumber);
  }

  if (!message.contentMarked) {
    return <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS}>{message.content}</ReactMarkdown>;
  }
  return (
    <ReactMarkdown
      remarkPlugins={MARKDOWN_PLUGINS}
      components={{
        a: ({ href, children }) => {
          if (href?.startsWith('#cite-')) {
            return <sup className="citation-chip">{children}</sup>;
          }
          return (
            <a href={href} target="_blank" rel="noreferrer">
              {children}
            </a>
          );
        },
      }}
    >
      {markCitations(message.contentMarked, sourceNumberByMarker)}
    </ReactMarkdown>
  );
}

// Fire-and-forget: the thumbs never block the UI or surface request state —
// a failed write only logs. The rating lands on the query log via queryId.
const sendFeedback = (message: BotMessage, rating: FeedbackRating): void => {
  fetch(`${process.env.REACT_APP_BACKEND_URL}/chatbot/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      queryId: message.queryId,
      question: message.question,
      answer: message.content,
      rating,
    }),
  }).catch(error => {
    console.error('feedback request failed', error);
  });
};

export default function ChatInterface() {
  const { isMobile, user, roles } = useAuth();

  // Data mode is invisible without the permission — no disabled tab, no hint
  // that another mode exists. Users who don't have it see exactly the chatbot
  // they see today.
  const canUseDataMode = Array.isArray(roles?.permissions)
    ? roles.permissions.includes(DATA_MODE_PERMISSION)
    : false;

  const [mode, setMode] = useState<ChatMode>('documents');

  // Follow-up context for Data mode. This is the last executed QUERY PLAN —
  // a small structured object like {operation:"list",filters:[…]} — not the
  // rows it returned. Echoing it back lets "only show the ones expiring this
  // year" refine the previous question without any employee data making a
  // round trip. The server re-validates it on arrival, so it carries no
  // authority; it's context, not a token.
  const [lastDataQuery, setLastDataQuery] = useState<unknown>(null);
  const [lastDataQuestion, setLastDataQuestion] = useState<string | null>(null);

  // First name comes from the /session user: the backend derives it from the
  // id_token's given_name claim (falling back to splitting the combined
  // name), so no client-side token decoding is needed — the tokens live in
  // httpOnly cookies and are never exposed to the frontend.
  const firstName = user?.firstName || 'You';

  const [messages, setMessages]         = useState<Message[]>([]);
  const [query, setQuery]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [queryHistory, setQueryHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [dots, setDots] = useState(0);

  // Per-bot-message thumbs selection, keyed by message index. Absent = neutral.
  const [feedback, setFeedback] = useState<Record<number, FeedbackChoice>>({});
  // The transient pop animation over the pressed thumb; `key` remounts it so
  // rapid presses restart the animation instead of being swallowed.
  const [feedbackFlash, setFeedbackFlash] = useState<{
    messageIndex: number;
    choice: FeedbackChoice;
    key: number;
  } | null>(null);
  const flashKeyRef = useRef(0);

  // Indices of bot messages whose sources dropdown is expanded (all start
  // collapsed; each message toggles independently).
  const [openSources, setOpenSources] = useState<Set<number>>(new Set());

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // The session rehydrates on org switch and on re-login, so the permission can
  // disappear under a mounted chat. Fall back to Documents rather than leaving
  // a mode selected that the server would now refuse.
  useEffect(() => {
    if (!canUseDataMode) setMode('documents');
  }, [canUseDataMode]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (loading) {
      interval = setInterval(() => {
        setDots(prev => (prev + 1) % 4); // cycles 0 → 1 → 2 → 3 → 0
      }, 500);
    } else {
      setDots(0); // reset when done sending
    }

    return () => clearInterval(interval);
  }, [loading]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!query.trim()) return;

    const questionGiven = query;
    // Pin the mode for this exchange so switching tabs mid-request can't route
    // the response through the other mode's rendering.
    const askedInMode = mode;
    setQuery('');

    // save to history
    setQueryHistory(h => [...h, questionGiven]);
    setHistoryIndex(-1);

    // add user message
    setMessages(m => [...m, { sender: 'user', content: questionGiven }]);
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${ASK_ENDPOINT[askedInMode]}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(
            askedInMode === 'data'
              ? {
                  question: questionGiven,
                  previousQuery: lastDataQuery,
                  previousQuestion: lastDataQuestion,
                }
              : { question: questionGiven },
          ),
          credentials: 'include',
        }
      );

      // Data mode answers are markdown built server-side from the query result
      // — a sentence for a count, a table for a list. A refusal (unknown field,
      // protected field, unsupported question) also arrives as a readable
      // answer with a 200, so it renders as a normal bubble rather than the
      // generic "having trouble" error. No queryId comes back, so no thumbs
      // render — correct, there's no query log to rate.
      if (askedInMode === 'data') {
        const payload = await res.json().catch(() => ({} as any));
        const answerText =
          (typeof payload.answer === 'string' && payload.answer) ||
          payload.message ||
          payload.error ||
          "Data mode isn't available right now. Please try again later.";

        // Only a query that actually ran becomes the follow-up baseline. After
        // a refusal the previous good query stays in place, so "sort that by
        // expiry date" still refers to the last real result rather than to
        // nothing.
        if (payload.query) {
          setLastDataQuery(payload.query);
          setLastDataQuestion(questionGiven);
        }

        setMessages(m => [
          ...m,
          { sender: 'bot', content: answerText, question: questionGiven },
        ]);
        setLoading(false);
        setQuery('');
        return;
      }

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed with status ${res.status}`);
      }


      const data = await res.json();

      // History stores plain strings now — unwrap the legacy { explanation }
      // answer shape until the backend returns a bare string.
      const answerText =
        typeof data.answer === 'string'
          ? data.answer
          : data.answer?.explanation ?? '';

      // push bot answer
      setMessages(m => [
        ...m,
        {
          sender: 'bot',
          content: answerText,
          contentMarked: typeof data.answerMarked === 'string' ? data.answerMarked : undefined,
          citations: Array.isArray(data.citations) ? data.citations : undefined,
          sources: data.sources,
          queryId: data.queryId,
          question: questionGiven,
        },
      ]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setMessages(m => [
        ...m,
        { sender: 'bot', content: "I'm sorry, I'm having trouble answering at the moment. Please come back later." },
      ]);
    }

    setLoading(false);
    setQuery('');
  };

  // Thumbs handler: selecting fires-and-forgets the rating (1 up / -1 down) and
  // plays a small pop over the pressed thumb; pressing the selected thumb again
  // reverts it to neutral, which sends 0 (clear) without animating.
  const handleFeedback = (messageIndex: number, choice: FeedbackChoice) => {
    const message = messages[messageIndex];
    if (message?.sender !== 'bot' || !message.queryId) return;

    const isReverting = feedback[messageIndex] === choice;
    setFeedback(prev => {
      const next = { ...prev };
      if (isReverting) delete next[messageIndex];
      else next[messageIndex] = choice;
      return next;
    });
    sendFeedback(message, isReverting ? 0 : FEEDBACK_RATING[choice]);
    if (isReverting) return;
    flashKeyRef.current += 1;
    setFeedbackFlash({ messageIndex, choice, key: flashKeyRef.current });
  };

  const toggleSources = (messageIndex: number) => {
    setOpenSources(prev => {
      const next = new Set(prev);
      if (next.has(messageIndex)) next.delete(messageIndex);
      else next.add(messageIndex);
      return next;
    });
  };

  // navigate input history
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      const idx =
        historyIndex === -1
          ? queryHistory.length - 1
          : Math.max(0, historyIndex - 1);
      setHistoryIndex(idx);
      setQuery(queryHistory[idx] || '');
    }
    if (e.key === 'ArrowDown') {
      const idx =
        historyIndex >= 0 && historyIndex < queryHistory.length - 1
          ? historyIndex + 1
          : -1;
      setHistoryIndex(idx);
      setQuery(idx === -1 ? '' : queryHistory[idx]);
    }
  };

  return (
    <div className={`chat-interface${isMobile ? ' mobile' : ''}`}>
      {canUseDataMode && (
        <div className="chat-mode-switch" role="tablist" aria-label="Chat mode">
          {(['documents', 'data'] as ChatMode[]).map(m => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              className={`chat-mode-tab${mode === m ? ' chat-mode-tab-active' : ''}`}
              onClick={() => {
                if (m === mode) return;
                setMode(m);
                // A mode switch ends the Data thread. Coming back to Data
                // after asking about documents shouldn't silently refine a
                // query from several questions ago.
                setLastDataQuery(null);
                setLastDataQuestion(null);
              }}
              disabled={loading}
            >
              {m === 'documents' ? 'Documents' : 'Data'}
            </button>
          ))}
        </div>
      )}

      <div className="messages">
        {messages.map((m, i) => (
          <div key={i} className={`message ${m.sender}`}>
            <strong className='message-sender'>
              {m.sender === 'user'
                ? `${firstName}:`
                : 'AskMJ:'}
            </strong>
            {m.sender === 'user' ? (
              <p>{m.content}</p>
            ) : (
              <div className="bot-response">
                <div className="explanation">
                  <AnswerMarkdown message={m} />
                </div>

                {/* ONLY show thumbs on non-fallback answers that carry a queryId
                    (error bubbles have no log to rate). */}
                {m.content !== LEARNING_FALLBACK && m.queryId && (
                  <div className="feedback-row">
                    <button
                      type="button"
                      className={`feedback-thumb${
                        feedback[i] === 'up' ? ' feedback-thumb-selected-up' : ''
                      }`}
                      aria-label="Good answer"
                      aria-pressed={feedback[i] === 'up'}
                      onClick={() => handleFeedback(i, 'up')}
                    >
                      <ThumbsUp size={16} />
                      {feedbackFlash?.messageIndex === i &&
                        feedbackFlash.choice === 'up' && (
                          <span
                            key={feedbackFlash.key}
                            className="feedback-flash feedback-flash-up"
                            aria-hidden="true"
                            onAnimationEnd={() => setFeedbackFlash(null)}
                          >
                            <ThumbsUp size={16} />
                          </span>
                        )}
                    </button>
                    <button
                      type="button"
                      className={`feedback-thumb${
                        feedback[i] === 'down' ? ' feedback-thumb-selected-down' : ''
                      }`}
                      aria-label="Bad answer"
                      aria-pressed={feedback[i] === 'down'}
                      onClick={() => handleFeedback(i, 'down')}
                    >
                      <ThumbsDown size={16} />
                      {feedbackFlash?.messageIndex === i &&
                        feedbackFlash.choice === 'down' && (
                          <span
                            key={feedbackFlash.key}
                            className="feedback-flash feedback-flash-down"
                            aria-hidden="true"
                            onAnimationEnd={() => setFeedbackFlash(null)}
                          >
                            <ThumbsDown size={16} />
                          </span>
                        )}
                    </button>
                  </div>
                )}

                {Array.isArray(m.sources) && m.sources.length > 0 && (
                  <div className="bot-footer">
                    <hr className="bot-divider" />
                    <div className="bot-sources">
                      <button
                        type="button"
                        className="sources-toggle"
                        aria-expanded={openSources.has(i)}
                        onClick={() => toggleSources(i)}
                      >
                        <span
                          className={`sources-chevron${
                            openSources.has(i) ? ' sources-chevron-open' : ''
                          }`}
                        >
                          ▸
                        </span>
                        Sources ({m.sources.length})
                      </button>
                      {openSources.has(i) && (
                        <ol className="sources-list">
                          {m.sources.map((source, sourceIndex) => (
                            <li key={sourceIndex}>
                              <span className="source-number">{sourceIndex + 1}</span>
                              <span className="source-name">{source}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="message bot typing-row">
            <span
              className="typing-indicator"
              role="status"
              aria-live="polite"
              aria-label="AskMJ is typing…"
            >
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSubmit} className="input-form">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            mode === 'data'
              ? 'Ask a question about your organization data…'
              : 'Type your question here…'
          }
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            minWidth: '100px', // Enough space for "Sending..."
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            fontSize: '16px',
            fontFamily: 'inherit',
          }}
        >
          {loading ? (
            <>
              <span style={{ marginRight: '2px' }}>Sending</span>
              <span style={{ display: 'inline-block', width: '1ch', textAlign: 'left' }}>
                {'.'.repeat(dots)}
              </span>
            </>
          ) : (
            'Send'
          )}
        </button>
      </form>
    </div>
  );
}
